// ccp/src/lib/contactIds.js
//
// D31 (2026-09-14): which Connect contact id means what.
//
// A bounced / re-offered queued callback is a CHAIN of Connect contacts, not
// one contact: the original inbound call, then one new CALLBACK contact per
// re-offer, each carrying InitialContactId (the chain ROOT) and
// PreviousContactId (the leg before it). Seen live today:
//   cca2a588 (INBOUND, missed) → 2cc441e3 → 364a2eda → 85db4f8c (CONNECTED)
// Only the leg that actually connected carries AgentInfo, and the wrap-up API
// binds on exactly that (DescribeContact.AgentInfo.Id). The console used to
// send getInitialContactId() — the root — so every save on a re-offered
// callback was refused with "no agent on contact". Correct refusal; wrong id.
//
// Rules, in one place so they cannot drift again:
//   contactIdForApi(contact)  — the LIVE leg's own id (what the agent is on).
//                               Use for the wrap-up API, the workspace's
//                               "Contact ID", and anything that must match
//                               Connect's record of the connected leg.
//   chainRootId(contact)      — InitialContactId; null when Streams has none.
//                               Informational (shown as the chain root).
//   notesContactId(contact)   — the chain root FIRST, on purpose: call notes
//                               typed during a bounced offer must survive to
//                               the leg that finally connects (D28 mirror).
//
// Every accessor is wrapped: a Streams getter can throw on a task/callback
// leg (D21), and a throw here must never take the workspace down.

export function liveContactId(contact) {
  try { return contact?.getContactId?.() || null; } catch { return null; }
}

export function chainRootId(contact) {
  try { return contact?.getInitialContactId?.() || null; } catch { return null; }
}

export function contactIdForApi(contact) {
  return liveContactId(contact) || chainRootId(contact) || null;
}

export function notesContactId(contact) {
  return chainRootId(contact) || liveContactId(contact) || null;
}

// True when this leg is a re-offer (its own id differs from the chain root).
export function isReoffer(contact) {
  const live = liveContactId(contact);
  const root = chainRootId(contact);
  return Boolean(live && root && live !== root);
}

// Rows 46/48 (2026-10-01): a leg whose id differs from the chain root is not
// always a re-offered callback. A queue transfer (console Transfer, since
// 2026-09-29) is also a new contact pointing back to the first leg, and was
// labelled "re-offered callback". Streams carries the contact's initiation
// method ('callback' for a re-offer, 'transfer' / 'queue_transfer' for a
// transfer; D31 live record: re-offers are CALLBACK). Lower-cased, null when
// Streams has none.
export function initiationMethodOf(contact) {
  try { const m = contact?.getInitiationMethod?.(); return m ? String(m).toLowerCase() : null; } catch { return null; }
}
export function isTransferMethod(method) {
  return method === 'transfer' || method === 'queue_transfer';
}
// What a linked leg is, for display: 'callback' | 'transfer' | 'linked'.
export function linkKind(method) {
  if (method === 'callback') return 'callback';
  if (isTransferMethod(method)) return 'transfer';
  return 'linked';
}
// One label for both screens (handover card, wrap-up), so they cannot drift.
export function linkLabel(method) {
  return { callback: 're-offered callback', transfer: 'transferred call', linked: 'linked contact' }[linkKind(method)];
}

export const shortId = id => (id ? `${String(id).slice(0, 8)}…` : '');
